import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { defaultTheme } from "./theme";
import { config } from "./utils/log";
import { URL_PREFIX_MAPPING, COUNTRY_CONFIGURATION, Feature } from "./config";
import {
  overwriteFaviconForGB,
  isValidFeatureForIAHDomain,
} from "./indiaWarehouseUtils";
import {
  createAsyncMessageBundleManager,
  LocalizationContextBuilder,
} from "@amzn/arb-tools";
import arbManifest from "../public/translations/arbManifest";
import { MbmProvider } from "@amzn/react-arb-tools";
import { InputCard } from "./InputCard";
import { StencilProvider } from "@amzn/stencil-react-components/context";
import {
  clearPreviousSession,
  previousSessionExists,
} from "./components/Chat/ChatContainer";
import ChatEndedPage from "./components/Chat/ChatEndedPage";
import { sendMetrics } from "./reporting/adobeMetrics";
import { getText } from "./utils/pantherHelper";

// disable all console messages for production
console.log = console.warn = console.error = () => {};

const DEFAULT_LOCALE_US = "en-US";
const HREF = new URL(window.location.href);
const LANGUAGE = HREF.searchParams.get("language");
const COUNTRY = HREF.searchParams.get("country");
const JOB_TYPE = HREF.searchParams.get("type");
const BYPASS_VA = HREF.searchParams.get("bypassVA") ? "true" : "false";
const INGRESS_POINT = HREF.searchParams.get("ingressPoint");
const URL_PREFIX =
  process.env.NODE_ENV === "development" ? "." : URL_PREFIX_MAPPING[COUNTRY];
const CONFIGURATION = COUNTRY_CONFIGURATION[COUNTRY]?.[LANGUAGE]?.[JOB_TYPE];
const agencyName = HREF.searchParams.get("agency");

// Redirect if:
// 1. Missing required parameters
// 2. Invalid configuration for the current domain (handled by isValidFeatureForIAHDomain)
if (
  !LANGUAGE ||
  !COUNTRY ||
  !CONFIGURATION ||
  !isValidFeatureForIAHDomain(COUNTRY, CONFIGURATION)
) {
  window.location = "https://hiring.amazon.com/contact-us#/";
}

// Set the favicon for India warehouse domain
overwriteFaviconForGB();
const LOCALE = CONFIGURATION.locale;
const localizationContextProvider = () =>
  new LocalizationContextBuilder()
    .withLocale(LOCALE)
    .withDefaultLocale(DEFAULT_LOCALE_US)
    .build();

const messageBundleManager = createAsyncMessageBundleManager({
  arbManifest,
  localizationContextProvider,
  resolveBundleUrl: (url) =>
    url
      ? `${URL_PREFIX}/translations/` + url
      : `${URL_PREFIX}/translations/translation.json`,
});

let bundle;
let settings;

(function (connect) {
  connect.LogManager && connect.LogManager.updateLoggerConfig(config);
  connect.ChatInterface = connect.ChatInterface || {};
  connect.ChatInterface.init = ({ containerId, ...props }) => {
    if (props.widgetType) {
      config.csmConfig = {
        widgetType: props.widgetType,
      };
    }
    config.features = {
      messageReceipts: {
        shouldSendMessageReceipts: true,
        throttleTime: 5000,
      },
    };
    connect.ChatSession.setGlobalConfig(config);
    ReactDOM.render(
      <MbmProvider mbm={messageBundleManager}>
        <BrowserRouter>
          <App
            {...props}
            bundle={bundle}
            locale={LOCALE}
            typingEnabledForBotInteraction={
              CONFIGURATION.typingEnabledForBotInteraction
            }
            feature={CONFIGURATION.feature}
            inputEditorDir={CONFIGURATION.inputEditorDirection}
          />
        </BrowserRouter>
      </MbmProvider>,
      document.getElementById(containerId) || document.getElementById("root")
    );
  };

  connect.ChatInterface.getCurrentTheme = () => {
    return defaultTheme;
  };

  window.connect = connect;
})(window.connect || {});

async function initChatApp(contactInfo) {
  bundle = await messageBundleManager.getBundle("strings");

  await sendMetrics({
    country: CONFIGURATION.country,
    language: CONFIGURATION.language,
    business: CONFIGURATION.business,
    channel: CONFIGURATION.channel,
    ingressPoint: INGRESS_POINT,
    event: "start chat contact",
  });

  connect.ChatInterface.init({
    containerId: "root", // This is the id of the container where you want the widget to reside
  });

  // Determine if the contactInfo is a phone number or email based on the feature
  const isPhoneNumber = CONFIGURATION.feature === "GRIEVANCE_REPORTING_PHONE";

  const contactAttributes = {
    country: CONFIGURATION.country,
    language: CONFIGURATION.language,
    business: CONFIGURATION.business,
    channel: CONFIGURATION.channel,
    virtualAssistantModuleBypass: BYPASS_VA,
    ingressPointURL:
      process.env.NODE_ENV === "development" ? "localhost:3000" : HREF.href,
    ...(INGRESS_POINT && { ingressPoint: INGRESS_POINT }),
    // Set either candidateEmailAddress or candidatePhoneNumber based on the feature
    ...(isPhoneNumber
      ? { candidatePhoneNumber: contactInfo }
      : { candidateEmailAddress: contactInfo }),
    // Add agency parameter if present in URL.
    // TODO: add validations using list of valid agencies from business.
    ...(agencyName && {
      agency: agencyName,
    }),
  };

  connect.ChatInterface.initiateChat(
    {
      name: contactInfo,
      region: settings.region,
      apiGatewayEndpoint: settings.apiGatewayEndpoint,
      contactAttributes: JSON.stringify(contactAttributes),
      featurePermissions: {
        ATTACHMENTS: false, // this is the override flag from user for attachments
      },
      supportedMessagingContentTypes: "text/plain", // enable rich messaging
      // we only need the last part of the contact flow
      // https://docs.aws.amazon.com/connect/latest/APIReference/API_StartChatContact.html
      contactFlowId: settings.contactFlowId.split("/")[3],
      instanceId: settings.instanceId,
      locale: LOCALE,
      country: COUNTRY,
      language: LANGUAGE,
      jobType: JOB_TYPE,
    },
    successHandler,
    failureHandler
  );
}

function successHandler(chatSession) {
  console.log("success!");
  document.getElementById("section-chat").style.opacity = 1;

  chatSession.onChatDisconnected(function (_data) {
    document.getElementById("root")?.remove();

    ReactDOM.render(
      <ChatEndedPage
        bundle={bundle}
        configuration={CONFIGURATION}
        ingressPoint={INGRESS_POINT}
      />,
      document.getElementById("chat-closure-message")
    );
  });
}

function failureHandler(error) {
  console.log("There was an error: ");
  console.log(error);
}

async function initializeApp() {
  // Fetch settings first
  settings = await fetch(`${URL_PREFIX}/settings.json`).then((result) =>
    result.json()
  );

  // Set dynamic page title based on feature type, using translated string if available
  const isGrievancePhone =
    CONFIGURATION?.feature === Feature.GRIEVANCE_REPORTING_PHONE;
  const titleKey = isGrievancePhone ? "page-title-grievance" : "page-title";
  const defaultTitle = isGrievancePhone
    ? "Candidate Care - Chat"
    : "Amazon Candidate Care - Chat";
  try {
    bundle = await messageBundleManager.getBundle("strings");
    const translatedTitle = getText(bundle, titleKey);
    document.title = translatedTitle || defaultTitle;
  } catch (_e) {
    document.title = defaultTitle;
  }

  if (previousSessionExists(LANGUAGE, COUNTRY, JOB_TYPE)) {
    // continue previous chat session
    initChatApp("").then((r) => {});
  } else {
    // clear the cache and start a new chat session
    clearPreviousSession();
    ReactDOM.render(
      <MbmProvider mbm={messageBundleManager}>
        <StencilProvider>
          <InputCard
            initChatApp={initChatApp}
            inputEditorDir={CONFIGURATION.inputEditorDirection}
            feature={CONFIGURATION.feature}
            language={CONFIGURATION.language}
            country={CONFIGURATION.country}
            business={CONFIGURATION.business}
            channel={CONFIGURATION.channel}
            ingressPoint={INGRESS_POINT}
            agencyName={agencyName}
            settings={settings}
            locale={CONFIGURATION.locale}
          />
        </StencilProvider>
      </MbmProvider>,
      document.getElementById("root")
    );
  }
}

// Initialize the app
initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
